/**
 * /signup-tenant — створення нового tenant'а для вже залогіненого user'а.
 *
 * Відрізняється від /signup тим, що /signup створює одночасно user + tenant
 * (для нових реєстрацій). Тут user уже існує — створюємо лише tenant з
 * OWNER membership.
 *
 * Викликається з /no-tenants як «Створити власний бізнес».
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createTenant, type CreateTenantResponse } from '@/api/auth';
import { useAuth } from '@/auth/store';

export default function SignupTenantPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setActiveTenant = useAuth((s) => s.setActiveTenant);

  const [tenantName, setTenantName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation<CreateTenantResponse, Error>({
    mutationFn: () =>
      createTenant({
        tenant_name: tenantName,
        billing_email: billingEmail || undefined,
      }),
    onSuccess: (data) => {
      setActiveTenant(data.id);
      // Інвалідуємо /my-tenants, щоб refetch підтягнув новий запис.
      queryClient.invalidateQueries({ queryKey: ['my-tenants'] });
      navigate('/', { replace: true });
    },
    onError: (e) => setError(String(e)),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!tenantName.trim()) {
      setError('Введіть назву бізнесу');
      return;
    }
    mut.mutate();
  }

  return (
    <div className="login">
      <form
        onSubmit={onSubmit}
        style={{ width: 480, padding: '2rem', background: 'white' }}
      >
        <h1 style={{ margin: '0 0 1.25rem', fontSize: '1.3rem' }}>
          Створити новий бізнес
        </h1>
        <p style={{ marginTop: 0, color: 'var(--text-secondary)' }}>
          Ви станете власником (OWNER) цього клієнта і зможете запрошувати
          інших користувачів.
        </p>
        <div className="field">
          <label>Назва бізнесу / ТОВ / ФОП</label>
          <input
            value={tenantName}
            onChange={(e) => setTenantName(e.target.value)}
            placeholder="ТОВ Ромашка"
            autoFocus
            required
          />
        </div>
        <div className="field">
          <label>Email для рахунків (опційно)</label>
          <input
            type="email"
            value={billingEmail}
            onChange={(e) => setBillingEmail(e.target.value)}
            placeholder="billing@example.com"
          />
        </div>
        <button
          type="submit"
          className="primary"
          style={{ width: '100%' }}
          disabled={mut.isPending}
        >
          {mut.isPending ? 'Створення…' : 'Створити'}
        </button>
        {error && <div className="error">{error}</div>}
      </form>
    </div>
  );
}
